import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Sparkles, LayoutDashboard, FolderOpen, Plus, CreditCard, ChevronRight, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// TODO: Uncomment when ready to enable dark mode
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { getSession, refreshSession } from "@/lib/session";
import { signOut, getSupabaseSession } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export function DashboardHeader() {
  const [location, setLocation] = useLocation();
  const [credits, setCredits] = useState(0);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Load credits
    const session = getSession();
    setCredits(session.credits);

    // Get user email from Supabase
    getSupabaseSession().then((session) => {
      if (session?.user?.email) {
        setUserEmail(session.user.email);
      }
    });

    // Refresh credits from API
    refreshSession().then((session) => {
      setCredits(session.credits);
    });

    // Listen for storage changes to update credits
    const handleStorage = () => {
      const session = getSession();
      setCredits(session.credits);
    };
    window.addEventListener("storage", handleStorage);

    // Also check periodically in case changes happen in same tab
    const interval = setInterval(handleStorage, 500);

    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, [location]);

  const handleLogout = async () => {
    try {
      await signOut();

      // Clear localStorage
      localStorage.clear();

      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });

      // Redirect to landing page
      setLocation("/");
    } catch (error) {
      toast({
        title: "Logout failed",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  const getCreditColor = (count: number) => {
    if (count === 0) return "bg-destructive text-destructive-foreground";
    if (count <= 2) return "bg-amber-500 text-white";
    return "bg-primary text-primary-foreground";
  };

  const getBreadcrumbs = () => {
    if (location === "/dashboard") return null;
    if (location === "/posts") return { current: "My Requests" };
    if (location === "/create") return { current: "New Request" };
    if (location.startsWith("/post/")) {
      const postId = location.split("/")[2];
      return { parent: { label: "My Requests", href: "/posts" }, current: `Request #${postId}` };
    }
    return null;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <>
      {/* Desktop/Tablet Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo */}
            <Link href="/dashboard">
              <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" data-testid="link-logo">
                <img
                  src="/logos/logo.webp"
                  srcSet="/logos/logo-84w.webp 84w, /logos/logo-168w.webp 168w"
                  sizes="(max-width: 768px) 40px, 48px"
                  width="84"
                  height="84"
                  alt="PhotoshopRequest.app"
                  className="h-10 md:h-12 w-auto"
                />
                <span
                  className="text-lg md:text-xl font-bold hidden sm:block bg-gradient-to-r from-[#2236c1] via-[#2196f3] to-[#00bcd4] bg-clip-text text-transparent"
                >
                  PhotoshopRequest
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <Link href="/dashboard">
                <Button
                  variant={location === "/dashboard" ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
              <Link href="/posts">
                <Button
                  variant={location === "/posts" || location.startsWith("/post/") ? "secondary" : "ghost"}
                  className="gap-2"
                  data-testid="nav-requests"
                >
                  <FolderOpen className="h-4 w-4" />
                  My Requests
                </Button>
              </Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* TODO: Uncomment when ready to enable dark mode */}
              <ThemeToggle />

              {/* <Badge
                className={`${getCreditColor(credits)} gap-1.5 px-2 md:px-3 py-1.5 font-display font-semibold`}
                data-testid="badge-credits"
              >
                <CreditCard className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{credits} {credits === 1 ? "Credit" : "Credits"}</span>
                <span className="sm:hidden">{credits}</span>
              </Badge> */}

              <Link href="/create">
                <Button
                  className="gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 h-9 md:h-10"
                  data-testid="button-new-request"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">New Request</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </Link>

              {/* User Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 md:h-10 md:w-10"
                    data-testid="button-user-menu"
                  >
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">My Account</p>
                      {userEmail && (
                        <p className="text-xs leading-none text-muted-foreground truncate">
                          {userEmail}
                        </p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="cursor-default">
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>{credits} {credits === 1 ? "Credit" : "Credits"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="menu-item-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Breadcrumbs Row */}
          {breadcrumbs && (
            <div className="pb-3 hidden md:block">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
                  </BreadcrumbItem>
                  {breadcrumbs.parent && (
                    <>
                      <BreadcrumbSeparator>
                        <ChevronRight className="h-4 w-4" />
                      </BreadcrumbSeparator>
                      <BreadcrumbItem>
                        <BreadcrumbLink href={breadcrumbs.parent.href}>
                          {breadcrumbs.parent.label}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    </>
                  )}
                  <BreadcrumbSeparator>
                    <ChevronRight className="h-4 w-4" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>{breadcrumbs.current}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="grid grid-cols-3 gap-1 p-2">
          <Link href="/dashboard">
            <Button
              variant={location === "/dashboard" ? "secondary" : "ghost"}
              className={`w-full flex-col h-16 gap-1 ${location === "/dashboard" ? "bg-primary/10" : ""}`}
              data-testid="mobile-nav-dashboard"
            >
              <LayoutDashboard className={`h-5 w-5 ${location === "/dashboard" ? "text-primary" : ""}`} />
              <span className={`text-xs ${location === "/dashboard" ? "text-primary font-semibold" : ""}`}>
                Dashboard
              </span>
            </Button>
          </Link>

          <Link href="/posts">
            <Button
              variant={location === "/posts" || location.startsWith("/post/") ? "secondary" : "ghost"}
              className={`w-full flex-col h-16 gap-1 ${location === "/posts" || location.startsWith("/post/") ? "bg-primary/10" : ""}`}
              data-testid="mobile-nav-requests"
            >
              <FolderOpen className={`h-5 w-5 ${location === "/posts" || location.startsWith("/post/") ? "text-primary" : ""}`} />
              <span className={`text-xs ${location === "/posts" || location.startsWith("/post/") ? "text-primary font-semibold" : ""}`}>
                Requests
              </span>
            </Button>
          </Link>

          <Link href="/create">
            <Button
              variant={location === "/create" ? "default" : "ghost"}
              className={`w-full flex-col h-16 gap-1 ${location === "/create"
                ? "bg-gradient-to-r from-primary to-primary/80"
                : ""
                }`}
              data-testid="mobile-nav-create"
            >
              <Plus className={`h-6 w-6 ${location === "/create" ? "text-primary-foreground" : ""}`} />
              <span className={`text-xs font-semibold ${location === "/create" ? "text-primary-foreground" : ""}`}>
                New
              </span>
            </Button>
          </Link>
        </div>
      </nav>

      {/* Bottom nav spacer for mobile */}
      <div className="md:hidden h-20" />
    </>
  );
}

